import { sql } from 'drizzle-orm';
import {
  pgTable,
  index,
  uniqueIndex,
  jsonb,
  timestamp,
  varchar,
  text,
  integer,
  decimal,
  boolean,
  uuid,
  primaryKey,
  date,
  uniqueIndex
} from "drizzle-orm/pg-core";
import { complianceRequirementTypeEnum, licenseStatusEnum } from './enums';
import { organizations } from './organizations';
import { users } from './users';

// Firm Licenses table - tracks company-level state licenses for public adjusting firms
export const firmLicenses = pgTable("firm_licenses", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  state: varchar("state", { length: 2 }).notNull(), // Two-letter state code
  licenseNumber: varchar("license_number").notNull(),
  licenseName: varchar("license_name"), // Name on license (may differ from company name)
  issueDate: date("issue_date"),
  expirationDate: date("expiration_date"),
  status: varchar("status").default("active"), // active, expired, suspended, pending
  licenseType: varchar("license_type").default("firm"), // firm, business_entity, agency
  notes: text("notes"),
  documentUrl: varchar("document_url"), // Link to uploaded license document
  bondNumber: varchar("bond_number"), // Surety bond number
  bondAmount: decimal("bond_amount", { precision: 12, scale: 2 }), // Surety bond amount
  bondExpirationDate: date("bond_expiration_date"), // Surety bond expiration date
  officeAddress: text("office_address"), // Company office address in this state
  officeCity: varchar("office_city"),
  officeState: varchar("office_state"),
  officeZip: varchar("office_zip"),
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  updatedBy: varchar("updated_by").references(() => users.id),
}, (table) => [
  index("idx_firm_licenses_state").on(table.state),
  index("idx_firm_licenses_status").on(table.status),
]);


// One row per alert already raised for an adjuster's license: the holder, the person told, the state, the expiry date
// the alert was for and the lead time (30 or 7 days). The unique index is what stops a duplicate alert, even when two
// runs overlap: a run inserts the row first and only notifies when the insert was new.
export const licenseExpiryAlerts = pgTable("license_expiry_alerts", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: uuid("org_id").notNull().references(() => organizations.id, { onDelete: "cascade" }),
  holderUserId: varchar("holder_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  recipientUserId: varchar("recipient_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  state: varchar("state", { length: 2 }).notNull(),
  expirationDate: date("expiration_date").notNull(),
  leadDays: integer("lead_days").notNull(),
  emailSentAt: timestamp("email_sent_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  uniqueIndex("license_expiry_alerts_unique_alert").on(
    table.orgId, table.holderUserId, table.recipientUserId, table.state, table.expirationDate, table.leadDays,
  ),
  index("license_expiry_alerts_org_idx").on(table.orgId),
]);

export type LicenseExpiryAlert = typeof licenseExpiryAlerts.$inferSelect;
export type InsertLicenseExpiryAlert = typeof licenseExpiryAlerts.$inferInsert;


// State licensing requirements - reference data for which states require firm licensing
export const stateLicensingRequirements = pgTable("state_licensing_requirements", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  state: varchar("state", { length: 2 }).notNull().unique(), // Two-letter state code
  stateName: varchar("state_name").notNull(),
  requiresFirmLicense: boolean("requires_firm_license").default(false),
  requiresIndividualLicense: boolean("requires_individual_license").default(true),
  licenseDisclosureRequired: boolean("license_disclosure_required").default(true), // Must include license # in contracts
  maxContractFeePercent: decimal("max_contract_fee_percent", { precision: 5, scale: 2 }), // State-mandated max fee
  contractCancellationDays: integer("contract_cancellation_days"), // Days client can cancel without penalty
  additionalRequirements: text("additional_requirements"), // Notes about special state requirements
  regulatoryAgency: varchar("regulatory_agency"), // Name of regulating body (e.g., "Department of Insurance")
  regulatoryUrl: varchar("regulatory_url"), // Link to state regulations
  lastUpdated: timestamp("last_updated").defaultNow(),
});


// TypeScript interfaces for PA license structure (stored in user's publicAdjusterLicenses JSONB)
export interface PALicense {
  state: string;
  licenseNumber: string;
  issueDate?: string;
  expirationDate?: string;
  status?: 'active' | 'expired' | 'suspended' | 'pending';
  licenseType?: string; // resident, non-resident, reciprocal
}


// State licensing information table
export const stateLicenses = pgTable("state_licenses", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  state: varchar("state", { length: 2 }).notNull(), // Two-letter state code
  stateName: varchar("state_name").notNull(),
  
  // PA Licensing
  paLicenseNumber: varchar("pa_license_number"),
  paLicenseStatus: licenseStatusEnum("pa_license_status").default("not_licensed"),
  paLicenseExpiry: timestamp("pa_license_expiry"),
  paClaimLimits: decimal("pa_claim_limits", { precision: 12, scale: 2 }), // Maximum claim value for PA work
  
  // Appraisal Licensing
  appraisalLicenseNumber: varchar("appraisal_license_number"),
  appraisalLicenseStatus: licenseStatusEnum("appraisal_license_status").default("not_licensed"),
  appraisalLicenseExpiry: timestamp("appraisal_license_expiry"),
  appraisalRequired: boolean("appraisal_required").default(true), // If all appraisals require license
  
  // State-Specific Rules
  requiresLicense: boolean("requires_license").default(true),
  allowsReciprocity: boolean("allows_reciprocity").default(false),
  specialRequirements: jsonb("special_requirements"), // State-specific requirements
  notes: text("notes"),
  
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_state_licenses_state").on(table.state),
  index("idx_state_licenses_pa_status").on(table.paLicenseStatus),
  index("idx_state_licenses_appraisal_status").on(table.appraisalLicenseStatus),
]);


// State Statutes Library - Jurisdiction-specific insurance regulations
export const stateStatutes = pgTable("state_statutes", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Jurisdiction
  state: varchar("state", { length: 2 }).notNull(), // US state abbreviation
  stateFullName: varchar("state_full_name").notNull(),
  
  // Statute information
  statuteType: varchar("statute_type").notNull(), // 'filing_deadline', 'doi_procedure', 'licensing_requirement', 'appraisal_process', 'settlement_timeline', 'bad_faith', 'prompt_pay'
  statuteTitle: varchar("statute_title").notNull(),
  statuteCode: varchar("statute_code"), // e.g., "§ 624.155"
  
  // Content
  statuteText: text("statute_text").notNull(), // Full statute text
  plainLanguageSummary: text("plain_language_summary").notNull(), // Layman's explanation
  practicalApplication: text("practical_application"), // How adjusters should apply this
  
  // Key details
  timeframes: jsonb("timeframes"), // Deadlines, response times, etc.
  penalties: jsonb("penalties"), // Penalties for non-compliance
  exceptions: text("exceptions").array(), // Exceptions to the rule
  
  // Related statutes
  relatedStatutes: text("related_statutes").array(), // Related statute codes
  supercededBy: uuid("superceded_by").references((): any => stateStatutes.id), // If replaced by newer statute
  
  // Peril/claim type specificity
  applicablePerils: text("applicable_perils").array(), // Which perils this applies to
  applicableClaimTypes: text("applicable_claim_types").array(), // Which claim types
  
  // Validity and updates
  effectiveDate: date("effective_date"),
  expirationDate: date("expiration_date"),
  lastVerified: timestamp("last_verified"), // When we last verified this is current
  isActive: boolean("is_active").default(true),
  
  // Source and citations
  sourceUrl: text("source_url"), // Link to official statute
  citations: text("citations").array(), // Legal citations
  
  // Usage tracking
  timesReferenced: integer("times_referenced").default(0), // How often AI references this
  lastReferencedAt: timestamp("last_referenced_at"),
  
  // Maintenance
  addedBy: varchar("added_by").references(() => users.id),
  verifiedBy: varchar("verified_by").references(() => users.id),
  notes: text("notes"), // Internal notes
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_state_statutes_state").on(table.state),
  index("idx_state_statutes_type").on(table.statuteType),
  index("idx_state_statutes_active").on(table.isActive),
  index("idx_state_statutes_effective").on(table.effectiveDate),
]);


// =====================
// GDPR Compliance Tables
// =====================

// Data Retention Policies - Define how long data should be kept
export const retentionPolicies = pgTable("retention_policies", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  entityType: varchar("entity_type").notNull(), // audit_logs, timeline_events, documents, etc.
  retentionPeriodDays: integer("retention_period_days").notNull(), // How many days to keep
  isActive: boolean("is_active").default(true),
  deletionRules: text("deletion_rules"), // Additional rules or conditions
  description: text("description"), // Human-readable policy description
  lastEnforcedAt: timestamp("last_enforced_at"),
  createdBy: varchar("created_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_retention_policies_entity_type").on(table.entityType),
  index("idx_retention_policies_is_active").on(table.isActive),
]);


// Data Deletion Requests - Track GDPR deletion requests, and firm offboarding
// (request_type 'firm_offboarding', server/services/offboarding: one open request per firm).
export const dataDeletionRequests = pgTable("data_deletion_requests", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id, { onDelete: "set null" }),
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "set null" }), // Firm being offboarded
  requestType: varchar("request_type").notNull().default("user"), // user, firm_offboarding
  requestedBy: varchar("requested_by").references(() => users.id).notNull(), // Admin who requested
  status: varchar("status").notNull().default("pending"), // pending, in_progress, awaiting_grace_period, completed, failed, cancelled
  anonymize: boolean("anonymize").default(false), // Anonymize vs delete
  reason: text("reason"),
  deletedRecords: jsonb("deleted_records"), // Record counts by table
  errors: jsonb("errors"), // Any errors encountered
  steps: jsonb("steps"), // Firm offboarding: each step's status and counts
  gracePeriodEndsAt: timestamp("grace_period_ends_at"), // Firm offboarding: no delete/anonymize before this
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_deletion_requests_user_id").on(table.userId),
  index("idx_deletion_requests_status").on(table.status),
  index("idx_deletion_requests_created_at").on(table.createdAt),
  index("idx_deletion_requests_org_id").on(table.orgId),
  uniqueIndex("uq_deletion_requests_open_firm_offboarding")
    .on(table.orgId)
    .where(sql`request_type = 'firm_offboarding' AND status NOT IN ('completed', 'cancelled')`),
]);


// Breach Notifications - Track security breaches for GDPR Article 33
export const breachNotifications = pgTable("breach_notifications", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  detectedAt: timestamp("detected_at").notNull().defaultNow(),
  detectedBy: varchar("detected_by").references(() => users.id),
  breachType: varchar("breach_type").notNull(), // unauthorized_access, data_leak, ransomware, etc.
  description: text("description").notNull(),
  severity: varchar("severity").notNull().default("medium"), // low, medium, high, critical
  affectedUsers: jsonb("affected_users"), // Array of user IDs
  affectedRecords: integer("affected_records"), // Number of records affected
  containmentActions: jsonb("containment_actions"), // Array of actions taken
  notificationStatus: varchar("notification_status").notNull().default("pending"), // pending, notified, failed
  notifiedAt: timestamp("notified_at"),
  notifiedAuthorities: jsonb("notified_authorities"), // Array of {authority, notifiedAt, method}
  regulatoryDeadline: timestamp("regulatory_deadline"), // 72-hour GDPR deadline
  resolutionNotes: text("resolution_notes"),
  resolvedAt: timestamp("resolved_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_breach_notifications_severity").on(table.severity),
  index("idx_breach_notifications_status").on(table.notificationStatus),
  index("idx_breach_notifications_detected_at").on(table.detectedAt),
  index("idx_breach_notifications_deadline").on(table.regulatoryDeadline),
]);


// State Compliance Rules - Reference table with statutory requirements per state
export const stateComplianceRules = pgTable("state_compliance_rules", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // State identification
  stateCode: varchar("state_code", { length: 2 }).notNull(),
  stateName: varchar("state_name").notNull(),
  
  // Requirement details
  requirementType: complianceRequirementTypeEnum("requirement_type").notNull(),
  baselineDays: integer("baseline_days").notNull(), // Days from trigger event
  dayType: varchar("day_type", { length: 20 }).default("calendar"), // "calendar" | "business"
  penaltyRate: varchar("penalty_rate", { length: 100 }),            // nullable; e.g. "18%", "prime+2%", "1.5%/month"

  // Recurrence (for requirements like periodic updates)
  isRecurring: boolean("is_recurring").default(false),
  recurrenceIntervalDays: integer("recurrence_interval_days"),
  
  // Legal citation
  statuteCitation: text("statute_citation").notNull(), // e.g., "Texas Insurance Code Chapter 542"
  statuteDescription: text("statute_description"), // Plain English description
  
  // Trigger event description
  triggerEventDescription: text("trigger_event_description"), // What starts the clock
  
  // Penalties/escalation info
  penaltyDescription: text("penalty_description"), // Consequences of carrier violation
  escalationAction: text("escalation_action"), // Recommended action if breached
  
  // Active status
  isActive: boolean("is_active").default(true),
  effectiveDate: date("effective_date"),
  expirationDate: date("expiration_date"),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_state_compliance_rules_state").on(table.stateCode),
  index("idx_state_compliance_rules_type").on(table.requirementType),
  index("idx_state_compliance_rules_state_type").on(table.stateCode, table.requirementType),
]);
